import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { officeAPI, parcelAPI, tripAPI, cityCodeAPI } from '../../services/api';
import { useOfficeAuth } from '../../context/OfficeAuthContext';
import { PARCEL_TYPES } from '../../constants/parcelTypes';

const inputCls = 'w-full border rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white';
const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

// Full (quadruple) name — at least 4 whitespace-separated parts.
const isFullName = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).length >= 4;

export default function OfficeNewParcelPage() {
  const navigate = useNavigate();
  const { session } = useOfficeAuth();
  const officeCity = session?.office?.city || '';

  const [step, setStep] = useState('form'); // form | payment | done

  // Sender — looked up by phone, same convention as OfficeNewBookingPage
  const [senderPhone, setSenderPhone] = useState('');
  const [senderName, setSenderName]   = useState('');
  const [senderNationalId, setSenderNationalId] = useState('');
  const [looking, setLooking] = useState(false);
  const [found, setFound]     = useState(null);

  // Receiver
  const [receiverName, setReceiverName]   = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');

  // Parcel details
  const [parcelType, setParcelType]     = useState('general_goods');
  const [description, setDescription]   = useState('');
  const [weightKg, setWeightKg]         = useState('');
  const [sizeCategory, setSizeCategory] = useState('medium');
  const [quantity, setQuantity]         = useState(1);
  const [isInsured, setIsInsured]       = useState(false);
  const [declaredValue, setDeclaredValue] = useState('');
  const [isFragile, setIsFragile]       = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [prohibitedItemsDeclared, setProhibitedItemsDeclared] = useState(false);
  const [quote, setQuote] = useState(null);

  // Trip search — "from" defaults to this office's own city, since the parcel
  // physically starts here.
  const [cities, setCities] = useState([]);
  const [fromCity, setFromCity] = useState('');
  const [toCity, setToCity]     = useState('');
  const [date, setDate]         = useState('');
  const [trips, setTrips]       = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const selectedTrip = trips.find((t) => t.id === selectedTripId) || null;

  const [destinationOffices, setDestinationOffices] = useState([]);
  const [destinationOfficeId, setDestinationOfficeId] = useState('');

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const [createdParcel, setCreatedParcel] = useState(null);
  const [bankAccounts, setBankAccounts]   = useState([]);
  const [receipt, setReceipt]             = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    // The super admin's curated city list (used for ticket-number codes too),
    // not trips-derived — that source is empty whenever this office's linked
    // companies have no upcoming scheduled trips right now.
    cityCodeAPI.getAll()
      .then((res) => setCities((res.data.data || []).map((c) => c.name).sort((a, b) => a.localeCompare(b, 'ar'))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (officeCity) setFromCity(officeCity);
  }, [officeCity]);

  // Destination offices load once a trip (and therefore a company) is chosen —
  // must be authorized for that company and sit in the trip's arrival city.
  useEffect(() => {
    setDestinationOfficeId('');
    if (!selectedTrip?.companyId) { setDestinationOffices([]); return; }
    parcelAPI.getOffices(selectedTrip.companyId, selectedTrip.toCity)
      .then((res) => setDestinationOffices(res.data.data || []))
      .catch(() => setDestinationOffices([]));
  }, [selectedTrip?.companyId, selectedTrip?.toCity]);

  const fetchQuote = useCallback(async () => {
    if (!weightKg) { setQuote(null); return; }
    try {
      const res = await parcelAPI.getQuote({
        weightKg, quantity, isInsured, declaredValue: declaredValue || 0, isFragile,
        tripId: selectedTripId || undefined,
      });
      setQuote(res.data.data);
    } catch { setQuote(null); }
  }, [weightKg, quantity, isInsured, declaredValue, isFragile, selectedTripId]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const lookupSender = async () => {
    if (!senderPhone || senderPhone.replace(/\D/g, '').length < 9) {
      return toast.warning('أدخل رقم هاتف صحيح');
    }
    setLooking(true);
    try {
      const res = await officeAPI.lookupCustomer({ mobile: senderPhone });
      if (res.data.data.found) {
        setFound(res.data.data);
        setSenderName(res.data.data.name || '');
        toast.success('تم العثور على العميل');
      } else {
        setFound(null);
        setSenderName('');
        toast.info('عميل جديد — أدخل الاسم');
      }
    } catch { toast.error('خطأ في البحث'); }
    finally { setLooking(false); }
  };

  const searchTrips = async (e) => {
    e.preventDefault();
    setSearching(true); setError(''); setTrips([]); setSelectedTripId(null);
    try {
      const res = await parcelAPI.getTrips({
        fromCity: fromCity || undefined, toCity: toCity || undefined,
        date: date || undefined, weightKg: weightKg || 0,
      });
      setTrips(res.data.data || []);
      if ((res.data.data || []).length === 0) toast.info('لا توجد رحلات متاحة بهذه المعايير');
    } catch { setError('تعذّر البحث عن رحلات مناسبة'); }
    finally { setSearching(false); }
  };

  const canSubmit = isFullName(senderName) && senderPhone.trim()
    && isFullName(receiverName) && receiverPhone.trim() && weightKg
    && (!selectedTripId || destinationOfficeId) && prohibitedItemsDeclared;

  const handleCreate = async () => {
    if (!isFullName(senderName)) return setError('يرجى إدخال الاسم الرباعي الكامل للمرسل');
    if (!isFullName(receiverName)) return setError('يرجى إدخال الاسم الرباعي الكامل للمستلم');
    if (selectedTripId && !destinationOfficeId) return setError('اختر مكتب الوجهة');
    if (!prohibitedItemsDeclared) return setError('يجب تأكيد فحص الطرد والتأكد من خلوه من مواد ممنوعة أو خطرة');
    setCreating(true); setError('');
    try {
      const res = await officeAPI.createParcel({
        senderName: senderName.trim(), senderPhone, senderNationalId,
        receiverName: receiverName.trim(), receiverPhone,
        parcelType, description, weightKg, sizeCategory, quantity,
        isInsured, declaredValue, isFragile, specialInstructions,
        prohibitedItemsDeclared,
        tripId: selectedTripId || undefined, destinationOfficeId: destinationOfficeId || undefined,
      });
      setCreatedParcel(res.data.data);
      if (selectedTripId) {
        const infoRes = await tripAPI.getPaymentInfo(selectedTripId);
        setBankAccounts(infoRes.data.data?.bankAccounts || []);
        setStep('payment');
      } else {
        setStep('done');
      }
      toast.success('تم إنشاء طلب الطرد');
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر إنشاء طلب الطرد');
    } finally { setCreating(false); }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReceipt(file);
    setReceiptPreview(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
  };

  const handleUpload = async () => {
    if (!receipt) return toast.warning('يرجى اختيار صورة الإيصال');
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('receipt', receipt);
      await officeAPI.submitParcelPaymentSlip(createdParcel.id, fd);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر رفع الإيصال');
    } finally { setUploading(false); }
  };

  const reset = () => {
    setStep('form'); setError('');
    setSenderPhone(''); setSenderName(''); setSenderNationalId(''); setFound(null);
    setReceiverName(''); setReceiverPhone('');
    setParcelType('general_goods'); setDescription(''); setWeightKg(''); setSizeCategory('medium');
    setQuantity(1); setIsInsured(false); setDeclaredValue(''); setIsFragile(false); setSpecialInstructions('');
    setProhibitedItemsDeclared(false);
    setToCity(''); setDate(''); setTrips([]); setSelectedTripId(null);
    setDestinationOfficeId(''); setCreatedParcel(null); setBankAccounts([]); setReceipt(null); setReceiptPreview(null);
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-1">طرد جديد</h1>
      <p className="text-sm text-gray-500 mb-5">للعميل الذي يحضر الطرد مباشرة إلى المكتب — دون استخدام تطبيق المسافر</p>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}

      {step === 'form' && (
        <div className="space-y-4">
          {/* Sender */}
          <div className="bg-white rounded-2xl border p-5">
            <h2 className="font-bold text-gray-800 mb-3">بيانات المرسل</h2>
            <div>
              <label className={labelCls}>رقم الهاتف *</label>
              <div className="flex gap-2">
                <input dir="ltr" className={`flex-1 ${inputCls}`} placeholder="+249912345678"
                  value={senderPhone}
                  onChange={(e) => { setSenderPhone(e.target.value); setFound(null); setSenderName(''); }} />
                <button type="button" onClick={lookupSender} disabled={looking}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 rounded-xl text-sm font-semibold transition disabled:opacity-50">
                  {looking ? '...' : 'بحث'}
                </button>
              </div>
              {found && <p className="text-xs text-emerald-600 mt-1.5 font-medium">✓ عميل مسجل — تم ملء الاسم تلقائياً</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label className={labelCls}>الاسم الرباعي *</label>
                <input className={inputCls} value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="مثال: أحمد محمد عبدالله علي" />
                {senderName.trim() && !isFullName(senderName) && (
                  <p className="text-xs text-red-600 mt-1">مطلوب 4 أسماء على الأقل</p>
                )}
              </div>
              <div><label className={labelCls}>الرقم الوطني (اختياري)</label><input className={inputCls} value={senderNationalId} onChange={(e) => setSenderNationalId(e.target.value)} /></div>
            </div>
          </div>

          {/* Receiver */}
          <div className="bg-white rounded-2xl border p-5">
            <h2 className="font-bold text-gray-800 mb-3">بيانات المستلم</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>الاسم الرباعي *</label>
                <input className={inputCls} value={receiverName} onChange={(e) => setReceiverName(e.target.value)} placeholder="مثال: أحمد محمد عبدالله علي" />
                {receiverName.trim() && !isFullName(receiverName) && (
                  <p className="text-xs text-red-600 mt-1">مطلوب 4 أسماء على الأقل</p>
                )}
              </div>
              <div><label className={labelCls}>رقم الهاتف *</label><input dir="ltr" className={inputCls} value={receiverPhone} onChange={(e) => setReceiverPhone(e.target.value)} /></div>
            </div>
          </div>

          {/* Parcel */}
          <div className="bg-white rounded-2xl border p-5">
            <h2 className="font-bold text-gray-800 mb-3">بيانات الطرد</h2>
            <div className="flex gap-2 flex-wrap mb-4">
              {PARCEL_TYPES.map((t) => (
                <button key={t.key} type="button" onClick={() => setParcelType(t.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
                    parcelType === t.key ? 'bg-blue-700 text-white border-blue-700' : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div><label className={labelCls}>الوزن (كجم) *</label><input type="number" step="0.1" className={inputCls} value={weightKg} onChange={(e) => setWeightKg(e.target.value)} /></div>
              <div>
                <label className={labelCls}>الحجم</label>
                <select className={inputCls} value={sizeCategory} onChange={(e) => setSizeCategory(e.target.value)}>
                  <option value="small">صغير</option><option value="medium">متوسط</option><option value="large">كبير</option>
                </select>
              </div>
              <div><label className={labelCls}>الكمية</label><input type="number" min="1" className={inputCls} value={quantity} onChange={(e) => setQuantity(e.target.value)} /></div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600 self-end mb-2.5">
                <input type="checkbox" checked={isFragile} onChange={(e) => setIsFragile(e.target.checked)} /> قابل للكسر
              </label>
            </div>
            <div className="mb-3"><label className={labelCls}>الوصف</label><input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                <input type="checkbox" checked={isInsured} onChange={(e) => setIsInsured(e.target.checked)} /> تأمين على الطرد
              </label>
              {isInsured && (
                <div><label className={labelCls}>القيمة المصرح بها</label><input type="number" className={inputCls} value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} /></div>
              )}
            </div>
            <div><label className={labelCls}>تعليمات خاصة</label><textarea rows={2} className={inputCls} value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} /></div>

            {quote && (
              <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
                <p className="text-xs font-bold text-blue-800 mb-2">السعر التقديري</p>
                <div className="space-y-1 text-xs text-gray-700">
                  {quote.breakdown.map((b, i) => (
                    <div key={i} className="flex justify-between"><span>{b.label}</span><span className="tabular-nums">{Number(b.amount).toLocaleString()} ج.س</span></div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 pt-2 border-t border-blue-200 font-black text-blue-900">
                  <span>الإجمالي</span><span className="tabular-nums">{Number(quote.totalPrice).toLocaleString()} ج.س</span>
                </div>
              </div>
            )}
          </div>

          {/* Trip search */}
          <div className="bg-white rounded-2xl border p-5">
            <h2 className="font-bold text-gray-800 mb-3">اختيار رحلة</h2>
            <form onSubmit={searchTrips} className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className={labelCls}>من (مكتبكم)</label>
                <select className={inputCls} value={fromCity} onChange={(e) => setFromCity(e.target.value)}>
                  <option value="">— اختر المدينة —</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>إلى</label>
                <select className={inputCls} value={toCity} onChange={(e) => setToCity(e.target.value)}>
                  <option value="">— اختر المدينة —</option>
                  {cities.filter((c) => c !== fromCity).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>التاريخ (اختياري)</label><input type="date" className={inputCls} value={date} onChange={(e) => setDate(e.target.value)} /></div>
              <button type="submit" disabled={searching} className="sm:col-span-3 bg-blue-600 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-50 transition">
                {searching ? 'جاري البحث...' : 'بحث عن رحلات'}
              </button>
            </form>

            {trips.length > 0 && (
              <div className="space-y-2">
                {trips.map((t) => (
                  <label key={t.id} className={`flex items-center justify-between gap-3 border rounded-xl px-4 py-3 cursor-pointer transition-all ${
                    selectedTripId === t.id ? 'border-blue-700 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <div className="flex items-center gap-3">
                      <input type="radio" name="trip" checked={selectedTripId === t.id} onChange={() => setSelectedTripId(t.id)} />
                      <div className="text-sm">
                        <span className="font-bold text-gray-800">{t.fromCity} ← {t.toCity}</span>
                        <span className="text-gray-500 mx-2 text-xs">{new Date(t.departureTime).toLocaleString('ar-SD', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
                        <span className="text-blue-700 text-xs font-semibold">{t.company?.name}</span>
                      </div>
                    </div>
                    {t.cargoCapacity?.maxWeightKg != null && (
                      <span className="text-emerald-700 bg-emerald-50 border border-emerald-100 text-xs font-semibold px-2 py-1 rounded-lg flex-shrink-0">
                        📦 متاح {t.cargoCapacity.availableWeightKg} كجم
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
            {!selectedTrip && (
              <p className="text-xs text-gray-500 mt-3">يمكنك المتابعة بدون اختيار رحلة — سيبقى الطرد بانتظار تعيين رحلة لاحقاً.</p>
            )}
          </div>

          {/* Destination office — only once a trip is picked */}
          {selectedTrip && (
            <div className="bg-white rounded-2xl border p-5">
              <h2 className="font-bold text-gray-800 mb-3">مكتب التسليم ({selectedTrip.toCity})</h2>
              <select className={inputCls} value={destinationOfficeId} onChange={(e) => setDestinationOfficeId(e.target.value)}>
                <option value="">اختر المكتب</option>
                {destinationOffices.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.city}</option>)}
              </select>
              {destinationOffices.length === 0 && (
                <p className="text-xs text-red-600 mt-1.5">لا يوجد مكتب تابع لهذه الشركة في {selectedTrip.toCity}</p>
              )}
            </div>
          )}

          {/* Office staff attests the inspection, not just the sender —
              the office keeps the right to refuse a shipment regardless. */}
          <div className="bg-white rounded-2xl border p-5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" className="mt-0.5 w-4 h-4" checked={prohibitedItemsDeclared} onChange={(e) => setProhibitedItemsDeclared(e.target.checked)} />
              <span className="text-sm text-gray-700">
                أؤكد فحص الطرد والتأكد من أنه <strong>لا يحتوي</strong> على مواد ممنوعة أو خطرة أو غير قانونية.
              </span>
            </label>
          </div>

          <button type="button" onClick={handleCreate} disabled={creating || !canSubmit}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 disabled:opacity-40 transition">
            {creating ? '...جارٍ الإنشاء' : 'إنشاء طلب الطرد'}
          </button>
        </div>
      )}

      {step === 'payment' && createdParcel && (
        <div className="bg-white rounded-2xl border p-6">
          <h2 className="font-bold text-gray-800 mb-1">الدفع وإرسال الإيصال</h2>
          <p className="text-xs text-gray-500 mb-4">رقم التتبع: <span className="font-mono font-bold text-blue-700">{createdParcel.trackingNumber}</span></p>

          {bankAccounts.length > 0 ? (
            <div className="space-y-2 mb-4">
              {bankAccounts.map((acc, i) => (
                <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{acc.bankName}</span>
                  <div className="mt-2 text-sm"><span className="text-gray-500 text-xs">رقم الحساب: </span><span dir="ltr" className="font-mono font-bold">{acc.accountNumber}</span></div>
                  <div className="text-sm"><span className="text-gray-500 text-xs">اسم الحساب: </span>{acc.accountHolder}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mb-4">لم تُضف الشركة حسابات بنكية بعد.</p>
          )}

          <label className={`flex flex-col items-center justify-center w-full border-2 border-dashed rounded-xl p-5 cursor-pointer transition mb-4 ${receipt ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
            {receiptPreview ? (
              <img src={receiptPreview} alt="إيصال" className="max-h-40 rounded-lg object-contain mb-2" />
            ) : (
              <span className="text-3xl mb-2">📎</span>
            )}
            <span className={`text-sm font-medium ${receipt ? 'text-emerald-700' : 'text-gray-500'}`}>
              {receipt ? receipt.name : 'اضغط لاختيار صورة أو PDF لإيصال العميل'}
            </span>
            <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" onChange={handleReceiptChange} />
          </label>

          <button onClick={handleUpload} disabled={uploading || !receipt}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-40 transition">
            {uploading ? 'جاري الإرسال...' : 'إرسال إيصال الدفع للمراجعة'}
          </button>
        </div>
      )}

      {step === 'done' && createdParcel && (
        <div className="text-center bg-white rounded-2xl border p-8">
          <div className="text-5xl mb-3">✅</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">تم إنشاء طلب الطرد</h2>
          <p className="font-mono font-black text-2xl text-blue-700 mb-4">{createdParcel.trackingNumber}</p>
          <p className="text-sm text-gray-500 mb-6">
            {selectedTripId
              ? 'بانتظار مراجعة الشركة للدفع، ثم يمكن استلام الطرد من هذا المكتب.'
              : 'الطرد بانتظار تعيين رحلة — عودوا إلى قائمة الطرود لاحقاً لمتابعة حالته.'}
          </p>
          <div className="flex gap-3">
            <button onClick={reset} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition">طرد جديد</button>
            <button onClick={() => navigate('/office/parcels')} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-50 transition">قائمة الطرود</button>
          </div>
        </div>
      )}
    </div>
  );
}
